import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import Arial from "../assets/fonts/Arial.ttf";
import ArialBold from "../assets/fonts/Arial_Bold.ttf";

function formatUSPhoneNumber(phoneNumber) {
  if (!phoneNumber) return "";
  // Convert the input to a string in case a number is passed
  const phoneStr = phoneNumber.toString();

  // Check if the phone number has exactly 11 digits and starts with '1'
  if (phoneStr.length === 11 && phoneStr[0] === "1") {
    // Extract parts of the phone number
    const countryCode = phoneStr.slice(0, 1);
    const areaCode = phoneStr.slice(1, 4);
    const centralOfficeCode = phoneStr.slice(4, 7);
    const lineNumber = phoneStr.slice(7, 11);

    // Format the phone number
    return `${areaCode}-${centralOfficeCode}-${lineNumber}`;
  } else {
    // Return an error message if the input is not a valid 11-digit number
    return phoneNumber;
  }
}

Font.register({
  family: "Arial",
  fonts: [
    {
      src: Arial,
    },
    {
      src: ArialBold,
      fontWeight: "bold",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    padding: 5,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 0,
    margin: 0,
  },

  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 0,
    marginTop: 5,
  },

  logo: {
    width: 100,
    height: 25,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    fontFamily: "Arial",
  },

  footerTitle: {
    fontSize: 10,
    fontWeight: "bold",
    fontFamily: "Arial",
  },

  dateDetails: {
    alignItems: "center",
    flexDirection: "column",
    fontSize: "8px",
    borderTop: "1px solid black",
    borderRight: "1px solid black",
    borderLeft: "1px solid black",
    borderBottomWidth: 0,
    paddingTop: 8,
    paddingLeft: 8,
    paddingRight: 8,
    paddingBottom: 2,
  },

  textCenter: {
    textAlign: "center",
    fontWeight: "bold",
    fontFamily: "Arial",
  },
  section: {
    flexGrow: 1,
  },

  certificateHeaderDetails: {
    padding: "1px 2px 3px 2px",
    borderTop: "1px solid black",
    borderRight: "1px solid black",
    borderLeft: "1px solid black",
    borderBottomWidth: 0,
  },

  certificateHeaderDetailsText: {
    color: "#000",
    fontSize: 8,
    fontWeight: "bold",
    fontFamily: "Arial",
  },

  certificateDetails: {
    flexDirection: "row",
    padding: 0,
    borderTop: "1px solid black",
    borderRight: "1px solid black",
    borderLeft: "1px solid black",
    borderBottomWidth: 0,
  },

  heading4: {
    color: "#000",
    fontSize: 10,
    fontWeight: "bold",
    fontFamily: "Arial",
  },

  heading5: {
    color: "#000",
    fontSize: 8,
    fontWeight: "bold",
    fontFamily: "Arial",
  },

  heading6: {
    color: "#000",
    fontSize: 7,
    fontWeight: "bold",
    fontFamily: "Arial",
  },

  headingLight: {
    color: "#000",
    fontSize: 8,
    fontWeight: 400,
  },

  paragraphLight: {
    color: "#000",
    fontSize: 7,
    fontWeight: 400,
  },

  producerContainerText: {
    color: "#000",
    fontSize: 9,
    fontWeight: "bold",
    fontFamily: "Arial",
    width: "70%",
    paddingLeft: 30,
  },

  normalText: {
    color: "#000",
    fontSize: 8,
    fontWeight: "bold",
    fontFamily: "Arial",
  },

  checkboxContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 5,
  },
  checkbox: {
    width: 10,
    height: 10,
    marginRight: 5,
    borderWidth: 1,
    borderColor: "#000",
    padding: 1,
  },
  checkboxText: {
    fontSize: 12,
  },
});

// Create PDF component
const PDFDocument = ({ certificate }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Image style={styles.logo} src={certificate.logo} />
        <View>
          <Text style={styles.title}>{certificate.title}</Text>
        </View>
        <View style={styles.dateDetails}>
          <Text
            style={{ fontSize: 5, fontWeight: "bold", fontFamily: "Arial" }}
          >
            DATE (MM/DD/YYYY)
          </Text>
          <Text style={styles.textCenter}>{certificate.date}</Text>
        </View>
      </View>

      {/* Header Details */}
      <View style={styles.certificateHeaderDetails}>
        <Text style={styles.certificateHeaderDetailsText}>
          {certificate.headerDescription}
        </Text>
      </View>
      <View style={styles.certificateHeaderDetails}>
        <Text style={styles.certificateHeaderDetailsText}>
          IMPORTANT: {certificate.headerNote}
        </Text>
      </View>

      {/* Certificate Details */}
      <View style={styles.certificateDetails}>
        <View style={{ width: "50%", borderRight: "1px solid black" }}>
          {/* Producer */}
          <View style={{ padding: "5px 0px 10px 0px" }}>
            <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
              PRODUCER
            </Text>
            <Text style={styles.producerContainerText}>
              {certificate.producer}
            </Text>
          </View>
        </View>
        <View style={{ width: "50%" }}>
          <View>
            {/* Cell */}
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View>
                <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                  CONTACT
                </Text>
                <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                  Name
                </Text>
              </View>

              <Text style={{ ...styles.normalText, paddingLeft: "20px" }}>
                {certificate?.contactDetails?.name}
              </Text>
            </View>
            {/* Cell */}
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "55%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <View>
                  <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                    PHONE
                  </Text>
                  <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                    (A/C, No, Ext):
                  </Text>
                </View>

                <Text style={{ ...styles.normalText, paddingLeft: "20px" }}>
                  {formatUSPhoneNumber(certificate?.contactDetails?.phone)}
                </Text>
              </View>

              <View style={{ width: "45%", flexDirection: "row" }}>
                <View>
                  <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                    FAX
                  </Text>
                  <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                    (A/C, No, Ext):
                  </Text>
                </View>

                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate?.contactDetails?.fax}
                </Text>
              </View>
            </View>
            {/* Cell */}
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View>
                <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                  E-MAIL
                </Text>
                <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                  ADDRESS
                </Text>
              </View>

              <Text style={{ ...styles.normalText, paddingLeft: "20px" }}>
                {certificate?.contactDetails?.email}
              </Text>
            </View>
            {/* Cell */}
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "80%",
                  flexDirection: "row",
                  justifyContent: "center",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.heading6, paddingLeft: "20px" }}>
                  {certificate.insurer.type}
                </Text>
              </View>

              <View
                style={{
                  width: "20%",
                  flexDirection: "row",
                  justifyContent: "center",
                }}
              >
                <Text style={{ ...styles.heading6, paddingLeft: "10px" }}>
                  NAIC #
                </Text>
              </View>
            </View>
            {/* Cell */}
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "80%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <View>
                  <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                    INSURER A:
                  </Text>
                </View>

                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate.insurer.a.title}
                </Text>
              </View>

              <View
                style={{
                  width: "20%",
                  flexDirection: "row",
                  justifyContent: "center",
                }}
              >
                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate.insurer.a.value}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>

      {/* Contact Details */}
      <View style={{ ...styles.certificateDetails, borderTop: "none" }}>
        <View
          style={{
            width: "50%",
            borderRight: "1px solid black",
            borderBottom: "1px solid black",
          }}
        >
          <View style={{ paddingTop: "5px", borderTop: "1px solid black" }}>
            <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
              INSURED
            </Text>
            <Text style={styles.producerContainerText}>
              {certificate.insured}
            </Text>
          </View>
        </View>
        <View style={{ width: "50%", fontWeight: "bold", fontFamily: "Arial" }}>
          <View>
            {/* Cell */}
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "80%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <View>
                  <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                    INSURER B:
                  </Text>
                </View>

                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate.insurer.b.title}
                </Text>
              </View>

              <View
                style={{
                  width: "20%",
                  flexDirection: "row",
                  justifyContent: "center",
                }}
              >
                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate.insurer.b.value}
                </Text>
              </View>
            </View>

            {/* Cell */}
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "80%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <View>
                  <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                    INSURER C:
                  </Text>
                </View>

                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate.insurer.c.title}
                </Text>
              </View>

              <View
                style={{
                  width: "20%",
                  flexDirection: "row",
                  justifyContent: "center",
                }}
              >
                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate.insurer.c.value}
                </Text>
              </View>
            </View>

            {/* Cell */}
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "80%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <View>
                  <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                    INSURER D:
                  </Text>
                </View>

                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate.insurer.d.title}
                </Text>
              </View>

              <View
                style={{
                  width: "20%",
                  flexDirection: "row",
                  justifyContent: "center",
                }}
              >
                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate.insurer.d.value}
                </Text>
              </View>
            </View>

            {/* Cell */}
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "80%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <View>
                  <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                    INSURER E:
                  </Text>
                </View>

                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate.insurer.e.title}
                </Text>
              </View>

              <View
                style={{
                  width: "20%",
                  flexDirection: "row",
                  justifyContent: "center",
                }}
              >
                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate.insurer.e.value}
                </Text>
              </View>
            </View>

            {/* Cell */}
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "80%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <View>
                  <Text style={{ ...styles.heading6, paddingLeft: "5px" }}>
                    INSURER F:
                  </Text>
                </View>

                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate.insurer.f.title}
                </Text>
              </View>

              <View
                style={{
                  width: "20%",
                  flexDirection: "row",
                  justifyContent: "center",
                }}
              >
                <Text style={{ ...styles.normalText, paddingLeft: "10px" }}>
                  {certificate.insurer.f.value}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>

      {/* Headings */}
      <View style={{ flexDirection: "row" }}>
        <View style={{ width: "25%", flexDirection: "row" }}>
          <Text style={styles.heading4}>COVERAGES:{certificate.coverages}</Text>
        </View>
        <View style={{ width: "37.5%", flexDirection: "row" }}>
          <Text style={styles.heading4}>
            CERTIFICATE NUMBER:{certificate.certificateNumber}
          </Text>
        </View>
        <View style={{ width: "37.5%", flexDirection: "row" }}>
          <Text style={styles.heading4}>
            REVISION NUMBER:{certificate.revisionNumber}
          </Text>
        </View>
      </View>

      {/* Form Container */}
      <View style={{ border: "1px solid black" }}>
        <View style={{ padding: "1px 2px 0px 2px", height: "38px" }}>
          <Text style={styles.headingLight}>{certificate.policyText}</Text>
        </View>
        {/* Forms */}
        <View style={{ flexDirection: "row", borderTop: "0.5px solid black" }}>
          <View style={{ width: "5%", borderRight: "0.5px solid black" }}>
            <Text style={{ ...styles.heading6, textAlign: "center" }}>
              INSR
            </Text>
            <Text style={{ ...styles.heading6, textAlign: "center" }}>LTR</Text>
          </View>
          <View
            style={{
              width: "25%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading6, textAlign: "center" }}>
              TYPE OF INSURANCE
            </Text>
          </View>
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading6, textAlign: "center" }}>
              ADDL
            </Text>
            <Text style={{ ...styles.heading6, textAlign: "center" }}>
              INSD
            </Text>
          </View>
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading6, textAlign: "center" }}>
              SUBR
            </Text>
            <Text style={{ ...styles.heading6, textAlign: "center" }}>WVD</Text>
          </View>
          <View
            style={{
              width: "12%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading6, textAlign: "center" }}>
              POLICY NUMBER
            </Text>
          </View>
          <View
            style={{
              width: "10%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading6, textAlign: "center" }}>
              POLICY EFF
            </Text>
            <Text style={{ ...styles.heading6, textAlign: "center" }}>
              MM/DD/YYYY
            </Text>
          </View>
          <View
            style={{
              width: "10%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading6, textAlign: "center" }}>
              POLICY EXP
            </Text>
            <Text style={{ ...styles.heading6, textAlign: "center" }}>
              MM/DD/YYYY
            </Text>
          </View>
          <View style={{ width: "25%", justifyContent: "center" }}>
            <Text style={{ ...styles.heading6, textAlign: "center" }}>
              LIMITS
            </Text>
          </View>
        </View>
        {/* Row 1 */}
        <View style={{ flexDirection: "row", borderTop: "0.5px solid black" }}>
          {/* cell */}
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Text
              style={{
                ...styles.heading5,
                textAlign: "center",
              }}
            >
              {certificate?.generalLiability?.insurer}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "25%",
              borderRight: "0.5px solid black",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              GENERAL LIABILITY
            </Text>
            <View style={styles.checkboxContainer}>
              <View style={styles.checkbox}>
                {certificate.generalLiability.comLiab && (
                  <Image
                    style={{ width: "100%", height: "100%" }}
                    src={certificate.tickIcon}
                  />
                )}
              </View>
              <Text style={styles.headingLight}>
                Commercial General Liability
              </Text>
            </View>
            <View style={{ flexDirection: "row" }}>
              <View style={styles.checkboxContainer}>
                <View style={styles.checkbox}>
                  {certificate.generalLiability.claimsMade && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>
                <Text style={styles.headingLight}>CLAIMS-MADE</Text>
              </View>
              <View style={{ ...styles.checkboxContainer, paddingLeft: 4 }}>
                <View style={styles.checkbox}>
                  {certificate.generalLiability.occur && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>
                <Text style={styles.headingLight}>OCCUR</Text>
              </View>
            </View>
            <Text style={{ ...styles.headingLight, textAlign: "center" }}>
              GEN'L AGGREGATE LIMIT APPLIES PER
            </Text>
            <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
              <View style={styles.checkboxContainer}>
                <View style={styles.checkbox}>
                  {certificate.generalLiability.aggegrateLimit.policy && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>
                <Text style={styles.headingLight}>POLICY</Text>
              </View>
              <View style={{ ...styles.checkboxContainer, paddingLeft: 4 }}>
                <View style={styles.checkbox}>
                  {certificate.generalLiability.aggegrateLimit.project && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>

                <Text style={styles.headingLight}>PROJECT</Text>
              </View>
              <View style={{ ...styles.checkboxContainer, paddingLeft: 4 }}>
                <View style={styles.checkbox}>
                  {certificate.generalLiability.aggegrateLimit.loc && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>

                <Text style={styles.headingLight}>LOC</Text>
              </View>
              <View style={{ ...styles.checkboxContainer }}>
                <View style={styles.checkbox}>
                  {certificate.generalLiability.aggegrateLimit.other && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>

                <Text style={styles.headingLight}>OTHER</Text>
              </View>
            </View>
          </View>
          {/* cell */}
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.generalLiability.addlinsd}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.generalLiability.subrwvd}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "12%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.generalLiability.policyNumber}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "10%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.generalLiability.eef}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "10%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.generalLiability.exp}
            </Text>
          </View>
          {/* cell */}
          <View style={{ width: "28%" }}>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  EACH OCCURENCE
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.generalLiability.limits.eachOccurence && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.generalLiability.limits.eachOccurence && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.generalLiability.limits.eachOccurence}
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",

                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  DAMAGE TO RENTED
                </Text>
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  PREMISES (Ea occurence)
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.generalLiability.limits.damageToRentend && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.generalLiability.limits.damageToRentend && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.generalLiability.limits.damageToRentend}
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  MED EXP (Any one person)
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.generalLiability.limits.medExp && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.generalLiability.limits.medExp && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.generalLiability.limits.medExp}
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  PERSONAL & ADV INJURY
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.generalLiability.limits.advInjury && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.generalLiability.limits.advInjury && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.generalLiability.limits.advInjury}
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  GENERAL AGGREGATE
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.generalLiability.limits.aggregate && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.generalLiability.limits.aggregate && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.generalLiability.limits.aggregate}
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  PRODUCTS - COMP/OP AGG
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.generalLiability.limits.products && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.generalLiability.limits.products && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.generalLiability.limits.products}
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  {certificate.generalLiability.limits.additionalCoverage}
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.generalLiability.limits.additionalLimit && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.generalLiability.limits.additionalLimit && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.generalLiability.limits.additionalLimit}
                  </Text>
                )}
              </View>
            </View>
          </View>
        </View>
        {/* Row 2 */}
        <View style={{ flexDirection: "row", borderTop: "0.5px solid black" }}>
          {/* cell */}
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate?.automobilityLiability?.insurer}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "25%",
              borderRight: "0.5px solid black",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              AUTOMOBILE LIABILITY
            </Text>
            <View style={{ flexDirection: "row" }}>
              <View style={styles.checkboxContainer}>
                <View style={styles.checkbox}>
                  {certificate.automobilityLiability.anyAuto && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>
                <Text style={styles.headingLight}>ANY AUTO</Text>
              </View>
            </View>
            <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
              <View style={styles.checkboxContainer}>
                <View style={styles.checkbox}>
                  {certificate.automobilityLiability.ownedAutosOnly && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>
                <View>
                  <Text style={styles.headingLight}>OWNED</Text>
                  <Text style={styles.headingLight}>AUTOS ONLY</Text>
                </View>
              </View>
              <View style={{ ...styles.checkboxContainer, paddingLeft: 4 }}>
                <View style={styles.checkbox}>
                  {certificate.automobilityLiability.scheduledAutos && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>
                <View>
                  <Text style={styles.headingLight}>SCHEDULED</Text>
                  <Text style={styles.headingLight}>AUTOS</Text>
                </View>
              </View>
              <View style={{ ...styles.checkboxContainer }}>
                <View style={styles.checkbox}>
                  {certificate.automobilityLiability.hiredAutosOnly && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>
                <View>
                  <Text style={styles.headingLight}>HIRED</Text>
                  <Text style={styles.headingLight}>AUTOS ONLY</Text>
                </View>
              </View>
              <View style={{ ...styles.checkboxContainer, paddingLeft: 4 }}>
                <View style={styles.checkbox}>
                  {certificate.automobilityLiability.nonAwonedAutos && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>

                <View>
                  {/* {" "} */}
                  <Text style={styles.headingLight}>NON-OWNED</Text>
                  <Text style={styles.headingLight}>AUTOS ONLY</Text>
                </View>
              </View>
            </View>
          </View>
          {/* cell */}
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}></Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}></Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "12%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.automobilityLiability.policyNumber}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "10%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.automobilityLiability.eef}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "10%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.automobilityLiability.exp}
            </Text>
          </View>
          {/* cell */}
          <View style={{ width: "28%" }}>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  COMBINED SINGLE LIMIT
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.automobilityLiability.limits
                  .combineSingleLimit && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.automobilityLiability.limits
                  .combineSingleLimit && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {
                      certificate.automobilityLiability.limits
                        .combineSingleLimit
                    }
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  BODILY INJURY
                </Text>
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  (Per Person)
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.automobilityLiability.limits
                  .bodilyInjuryPerPerson && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.automobilityLiability.limits
                  .bodilyInjuryPerPerson && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {
                      certificate.automobilityLiability.limits
                        .bodilyInjuryPerPerson
                    }
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",

                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  BODILY INJURY
                </Text>
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  (Per accident)
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.automobilityLiability.limits
                  .bodilyInuryPerAccident && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.automobilityLiability.limits
                  .bodilyInuryPerAccident && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {
                      certificate.automobilityLiability.limits
                        .bodilyInuryPerAccident
                    }
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  PROPERTY DAMAGE
                </Text>
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  (Per accident)
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.automobilityLiability.limits.propertyDamage && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.automobilityLiability.limits.propertyDamage && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.automobilityLiability.limits.propertyDamage}
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  {certificate.automobilityLiability.limits.additionalCoverage}
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.automobilityLiability.limits.additionalLimit && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.automobilityLiability.limits.additionalLimit && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.automobilityLiability.limits.additionalLimit}
                  </Text>
                )}
              </View>
            </View>
          </View>
        </View>
        {/* Row 3 */}
        <View style={{ flexDirection: "row", borderTop: "0.5px solid black" }}>
          {/* cell */}
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate?.general?.insurer}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "25%",
              borderRight: "0.5px solid black",
            }}
          >
            <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
              <View style={styles.checkboxContainer}>
                <View style={styles.checkbox}>
                  {certificate.general.umbrellaLiab && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>
                <View>
                  <Text style={styles.headingLight}>UMBRELLA LIAB</Text>
                </View>
              </View>
              <View style={{ ...styles.checkboxContainer, paddingLeft: 4 }}>
                <View style={styles.checkbox}>
                  {certificate.general.occcur && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>
                <View>
                  <Text style={styles.headingLight}>OCCUR</Text>
                </View>
              </View>
              <View style={{ ...styles.checkboxContainer }}>
                <View style={styles.checkbox}>
                  {certificate.general.excessLiab && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>
                <Text style={styles.headingLight}>EXCESS LIAB</Text>
              </View>
              <View style={{ ...styles.checkboxContainer, paddingLeft: 4 }}>
                <View style={styles.checkbox}>
                  {certificate.general.claimsMade && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>

                <View>
                  {/* {" "} */}
                  <Text style={styles.headingLight}>CLAIMS-MADE</Text>
                </View>
              </View>
              <View style={{ ...styles.checkboxContainer, paddingLeft: 4 }}>
                <View style={styles.checkbox}>
                  {certificate.general.ded && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>

                <View>
                  {/* {" "} */}
                  <Text style={styles.headingLight}>DED</Text>
                </View>
              </View>
              <View style={{ ...styles.checkboxContainer, paddingLeft: 4 }}>
                <View style={styles.checkbox}>
                  {certificate.general.retentions && (
                    <Image
                      style={{ width: "100%", height: "100%" }}
                      src={certificate.tickIcon}
                    />
                  )}
                </View>

                <View>
                  {/* {" "} */}
                  <Text style={styles.headingLight}>RETENTION</Text>
                </View>
              </View>
            </View>
          </View>
          {/* cell */}
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate?.general?.addlinsd}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {" "}
              {certificate?.general?.subrwvd}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "12%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {" "}
              {certificate?.general?.policyNumber}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "10%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {" "}
              {certificate?.general?.eff}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "10%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {" "}
              {certificate?.general?.exp}
            </Text>
          </View>
          {/* cell */}
          <View style={{ width: "28%" }}>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                  paddingTop: "5px",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  EACH OCCURENCE
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  paddingTop: "5px",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.general.limits.eachOccurence && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.general.limits.eachOccurence && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.general.limits.eachOccurence}
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  borderRight: "0.5px solid black",
                  paddingTop: "5px",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  AGGREGATE
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  paddingTop: "5px",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.general.limits.aggregate && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.general.limits.aggregate && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.general.limits.aggregate}
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  borderRight: "0.5px solid black",
                  paddingTop: "5px",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  {certificate.general.limits.additionalCoverage}
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  paddingTop: "5px",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.general.limits.additionalLimit && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.general.limits.additionalLimit && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.general.limits.additionalLimit}
                  </Text>
                )}
              </View>
            </View>
          </View>
        </View>
        {/* Row 4 */}
        <View style={{ flexDirection: "row", borderTop: "0.5px solid black" }}>
          {/* cell */}
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.workers.insurer}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "25%",
              borderRight: "0.5px solid black",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                paddingLeft: 1,
              }}
            >
              <Text style={{ ...styles.heading5, textAlign: "center" }}>
                WORKERS COMPENSATION
              </Text>
              <Text style={{ ...styles.heading5, textAlign: "center" }}>
                AND EMPLOYERS' LIABILITY
              </Text>
              <View style={styles.checkboxContainer}>
                <View>
                  <Text style={styles.paragraphLight}>
                    ANYPROFIETOR/PARTNER/EXECUTIVE
                  </Text>
                  <Text style={styles.paragraphLight}>
                    OFFICER/MEMBEREXCLUDED
                  </Text>
                </View>
              </View>
              <View>
                <View style={{ display: "flex", flexDirection: "row" }}>
                  <View>
                    <Text style={{ ...styles.heading5, textAlign: "center" }}>
                      Mandatory in NH
                    </Text>
                    <Text style={styles.paragraphLight}>
                      If yes, describe under
                    </Text>
                    <Text style={styles.paragraphLight}>
                      DESCRIPTION OF OPERATIONS below
                    </Text>
                  </View>
                  <View
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Text style={{ ...styles.heading6, textAlign: "center" }}>
                      Y / N
                    </Text>
                    <View style={{ ...styles.checkbox, marginLeft: 8 }}>
                      {certificate.workers.executive && (
                        <Image
                          style={{ width: "100%", height: "100%" }}
                          src={certificate.tickIcon}
                        />
                      )}
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
          {/* cell */}
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.workers.addlinsd}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "5%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.workers.subrwvd}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "12%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.workers.policyNumber}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "10%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.workers.eff}
            </Text>
          </View>
          {/* cell */}
          <View
            style={{
              width: "10%",
              borderRight: "0.5px solid black",
              justifyContent: "center",
            }}
          >
            <Text style={{ ...styles.heading5, textAlign: "center" }}>
              {certificate.workers.exp}
            </Text>
          </View>
          {/* cell */}
          <View style={{ width: "28%" }}>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  flexDirection: "row",
                  borderRight: "0.5px solid black",
                }}
              >
                <View style={{ width: "100%", flexDirection: "row" }}>
                  <View
                    style={{ width: "50%", borderRight: "0.5px solid black" }}
                  >
                    <Text
                      style={{ ...styles.headingLight, paddingLeft: "5px" }}
                    >
                      PER
                    </Text>
                    <Text
                      style={{ ...styles.headingLight, paddingLeft: "5px" }}
                    >
                      STATUE
                    </Text>
                  </View>

                  <View style={{ width: "50%" }}>
                    <Text
                      style={{ ...styles.headingLight, paddingLeft: "5px" }}
                    >
                      OTH-
                    </Text>
                    <Text
                      style={{ ...styles.headingLight, paddingLeft: "5px" }}
                    >
                      ER
                    </Text>
                  </View>
                </View>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.workers.limits.perstatue && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.workers.limits.perstatue && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.workers.limits.perstatue}
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  E.L EACH ACCIDENT
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.workers.limits.eachAccident && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.workers.limits.eachAccident && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.workers.limits.eachAccident}
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                borderBottom: "0.5px solid black",
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  E.L DISEASE - EA EMPLOYEE
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.workers.limits.diseaseEaEmployee && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.workers.limits.diseaseEaEmployee && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.workers.limits.diseaseEaEmployee}
                  </Text>
                )}
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
              }}
            >
              <View
                style={{
                  width: "65%",
                  borderRight: "0.5px solid black",
                }}
              >
                <Text style={{ ...styles.headingLight, paddingLeft: "5px" }}>
                  E.L DISEASE - POLICY LIMIT
                </Text>
              </View>

              <View
                style={{
                  width: "35%",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                }}
              >
                {certificate.workers.limits.diseasePolicyLimit && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      paddingLeft: "2px",
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    $
                  </Text>
                )}
                {certificate.workers.limits.diseasePolicyLimit && (
                  <Text
                    style={{
                      ...styles.headingLight,
                      fontWeight: "bold",
                      fontFamily: "Arial",
                    }}
                  >
                    {certificate.workers.limits.diseasePolicyLimit}
                  </Text>
                )}
              </View>
            </View>
          </View>
        </View>
        {/* Row 5 */}
        <View style={{ borderTop: "0.5px solid black" }}></View>
        {!!certificate?.physicalDamage.policyNumber && (
          <View style={{ flexDirection: "row", minHeight: "0px" }}>
            {/* cell */}
            <View
              style={{
                width: "5%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  ...styles.heading5,
                  marginTop: "2px",
                  textAlign: "center",
                }}
              >
                {certificate?.physicalDamage?.insurer}
              </Text>
            </View>
            {/* cell */}
            <View
              style={{
                width: "25%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  paddingLeft: 1,
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    ...styles.heading6,
                    marginTop: "2px",
                    textAlign: "center",
                    alignItems: "center",
                  }}
                >
                  {!!certificate?.physicalDamage?.policyNumber &&
                    "Auto Physical Damage"}
                </Text>
                <View></View>
              </View>
            </View>
            {/* cell */}
            <View
              style={{
                width: "5%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  ...styles.heading5,
                  textAlign: "center",
                  marginTop: "2px",
                }}
              >
                {certificate?.physicalDamage?.addlinsd}
              </Text>
            </View>
            {/* cell */}
            <View
              style={{
                width: "5%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  ...styles.heading5,
                  textAlign: "center",
                  marginTop: "2px",
                }}
              >
                {certificate?.physicalDamage?.subrwvd}
              </Text>
            </View>
            {/* cell */}
            <View
              style={{
                width: "12%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  ...styles.heading5,
                  textAlign: "center",
                  marginTop: "2px",
                }}
              >
                {certificate?.physicalDamage.policyNumber}
              </Text>
            </View>
            {/* cell */}
            <View
              style={{
                width: "10%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  ...styles.heading5,
                  textAlign: "center",
                  marginTop: "2px",
                }}
              >
                {certificate?.physicalDamage.eef}
              </Text>
            </View>
            {/* cell */}
            <View
              style={{
                width: "10%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  ...styles.heading5,
                  textAlign: "center",
                  marginTop: "2px",
                }}
              >
                {certificate?.physicalDamage.exp}
              </Text>
            </View>
            {/* cell */}
            <View style={{ width: "28%", justifyContent: "center" }}>
              <Text
                style={{
                  ...styles.heading5,
                  paddingLeft: "5px",
                  alignItems: "center",
                }}
              >
                {certificate.physicalDamage.limit}
              </Text>
            </View>
          </View>
        )}
        {/* Row 6 */}
        {!!certificate?.motorTruckCargo.policyNumber && (
          <View style={{ flexDirection: "row", minHeight: "0px" }}>
            {/* cell */}
            <View
              style={{
                width: "5%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  ...styles.heading5,
                  textAlign: "center",
                  marginTop: "5px",
                  marginBottom: "2px",
                }}
              >
                {certificate?.motorTruckCargo?.insurer}
              </Text>
            </View>
            {/* cell */}
            <View
              style={{
                width: "25%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  paddingLeft: 1,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Text
                  style={{
                    ...styles.heading6,
                    marginBottom: "2px",
                    marginTop: "5px",
                    textAlign: "center",
                    alignItems: "center",
                  }}
                >
                  {!!certificate?.motorTruckCargo.policyNumber &&
                    "Motor Truck Cargo"}
                </Text>
                <View></View>
              </View>
            </View>
            {/* cell */}
            <View
              style={{
                width: "5%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  ...styles.heading5,
                  textAlign: "center",
                  marginTop: "5px",
                  marginBottom: "2px",
                }}
              >
                {certificate?.motorTruckCargo?.addlinsd}
              </Text>
            </View>
            {/* cell */}
            <View
              style={{
                width: "5%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  ...styles.heading5,
                  textAlign: "center",
                  marginTop: "5px",
                  marginBottom: "2px",
                }}
              >
                {certificate?.motorTruckCargo?.subrwvd}
              </Text>
            </View>
            {/* cell */}
            <View
              style={{
                width: "12%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  ...styles.heading5,
                  textAlign: "center",
                  marginTop: "5px",
                  marginBottom: "2px",
                }}
              >
                {certificate?.motorTruckCargo.policyNumber}
              </Text>
            </View>
            {/* cell */}
            <View
              style={{
                width: "10%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  ...styles.heading5,
                  textAlign: "center",
                  marginTop: "5px",
                  marginBottom: "2px",
                }}
              >
                {certificate?.motorTruckCargo.eef}
              </Text>
            </View>
            {/* cell */}
            <View
              style={{
                width: "10%",
                borderRight: "0.5px solid black",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  ...styles.heading5,
                  textAlign: "center",
                  marginTop: "5px",
                  marginBottom: "2px",
                }}
              >
                {certificate?.motorTruckCargo.exp}
              </Text>
            </View>
            {/* cell */}
            <View style={{ width: "28%" }}>
              <View
                style={{
                  flexDirection: "row",
                }}
              >
                <Text
                  style={{
                    ...styles.heading5,
                    paddingLeft: "5px",
                    marginTop: "5px",
                    marginBottom: "2px",
                  }}
                >
                  {certificate?.motorTruckCargo.limit}
                </Text>
              </View>
            </View>
          </View>
        )}
        {/* Row 6 - conditional row */}
        {!certificate?.physicalDamage.policyNumber &&
          !certificate?.motorTruckCargo.policyNumber && (
            <View style={{ flexDirection: "row", minHeight: "30px" }}>
              {/* cell */}
              <View
                style={{
                  width: "5%",
                  borderRight: "0.5px solid black",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{ ...styles.heading5, textAlign: "center" }}
                ></Text>
              </View>
              {/* cell */}
              <View
                style={{
                  width: "25%",
                  borderRight: "0.5px solid black",
                  justifyContent: "center",
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    paddingLeft: 1,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Text
                    style={{
                      ...styles.heading6,
                      marginBottom: "4px",
                      textAlign: "center",
                    }}
                  ></Text>
                  <View></View>
                </View>
              </View>
              {/* cell */}
              <View
                style={{
                  width: "5%",
                  borderRight: "0.5px solid black",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{ ...styles.heading5, textAlign: "center" }}
                ></Text>
              </View>
              {/* cell */}
              <View
                style={{
                  width: "5%",
                  borderRight: "0.5px solid black",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{ ...styles.heading5, textAlign: "center" }}
                ></Text>
              </View>
              {/* cell */}
              <View
                style={{
                  width: "12%",
                  borderRight: "0.5px solid black",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{ ...styles.heading5, textAlign: "center" }}
                ></Text>
              </View>
              {/* cell */}
              <View
                style={{
                  width: "10%",
                  borderRight: "0.5px solid black",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{ ...styles.heading5, textAlign: "center" }}
                ></Text>
              </View>
              {/* cell */}
              <View
                style={{
                  width: "10%",
                  borderRight: "0.5px solid black",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{ ...styles.heading5, textAlign: "center" }}
                ></Text>
              </View>
              {/* cell */}
              <View style={{ width: "28%" }}>
                <View
                  style={{
                    flexDirection: "row",
                  }}
                >
                  <Text
                    style={{ ...styles.heading5, paddingLeft: "5px" }}
                  ></Text>
                </View>
              </View>
            </View>
          )}
        {/* Row 7 */}
        <View style={{ height: "70px", borderTop: "0.5px solid black" }}>
          <Text
            style={{
              ...styles.heading6,
              paddingLeft: "5px",
              paddingTop: "2px",
            }}
          >
            DESCRIPTION OF OPERATIONS / LOCATIONS / VEHICLES (ACORD 101,
            Additional Remarks Schedule, may be attached if more space is
            required),
            {/* {certificate.formContainerDescription} */}
          </Text>

          <View
            style={{
              paddingLeft: "10px",
              paddingTop: "4px",
              flexDirection: "row",
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            <View style={{ width: "50%", paddingRight: "5px" }}>
              {certificate?.certificateFooter?.description
                ?.split("\n")
                ?.map((row, i) => {
                  if (i < 6)
                    return <Text style={{ ...styles.heading6 }}>{row}</Text>;
                })}
            </View>
            <View style={{ width: "50%" }}>
              {certificate?.certificateFooter?.description
                ?.split("\n")
                ?.map((row, i) => {
                  if (i > 5 && i < 12)
                    return <Text style={{ ...styles.heading6 }}>{row}</Text>;
                })}
            </View>
          </View>

          {/* <Text style={{ ...styles.heading4, paddingLeft: "10px" }}>
            1 Non Owned Trailor Valued %50,000
          </Text>

          <Text
            style={{
              ...styles.heading4,
              paddingLeft: "10px",
              paddingTop: "10px",
            }}
          >
            Motor Truck Cargo Coverage includes Reefer Coverage
          </Text> */}
        </View>
      </View>

      {/* Headings */}
      <View style={{ flexDirection: "row" }}>
        <View style={{ width: "50%", flexDirection: "row" }}>
          <Text style={styles.heading4}>CERTIFICATE HOLDER</Text>
        </View>
        <View style={{ width: "50%", flexDirection: "row" }}>
          <Text style={styles.heading4}>CANCELLATION</Text>
        </View>
      </View>

      {/* Certificate Holder  */}
      <View style={{ flexDirection: "row", border: "1px solid black" }}>
        <View
          style={{
            width: "50%",
            padding: "8px 5px",
            borderRight: "1px solid black",
          }}
        >
          {/* <Text style={styles.heading4}>Integrity Express Logistics</Text>
          <Text style={styles.heading4}>PO BOX 42275</Text> */}
          <Text style={styles.heading4}>
            {certificate?.certificateFooter?.certificateHolder}
          </Text>
        </View>
        <View style={{ width: "50%" }}>
          <View style={{ padding: "8px 5px" }}>
            <Text style={styles.heading5}>
              {/* SHOULD ANY OF THE ABOVE DESCRIBED POLICIES BE CANCELLED BEFORE THE
              EXPIRATION DATE THEREOF, NOTICE WILL BE DELIVERED IN ACCORDANCE
              WITH THE POLICY PROVISIONS */}
              {certificate?.certificateFooter?.cancellation}
            </Text>
          </View>
          <View style={{ padding: "5px 5px", borderTop: "1px solid black" }}>
            <Text style={styles.heading6}>AUTHORIZED REPRESENTATIVE</Text>
            {certificate.signatureImage && (
              <Image style={styles.logo} src={certificate.signatureImage} />
            )}
          </View>
        </View>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <View>
          <Text style={styles.heading4}>ACCORD 25 (2016/03)</Text>
        </View>
        <View>
          <Text style={styles.heading4}>
            The Accord name and logo are registered marks of ACCORD
          </Text>
        </View>
        <View></View>
      </View>
    </Page>
  </Document>
);

export default PDFDocument;
